import {increment} from '@packages/utilities/string-utils';
import {
  Expressions,
  Component,
  Types,
  matchExpression,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  Expression,
  EText,
  ERecord,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {weakMemo} from '@packages/utilities/memo';
import {concat, constant, emptyArray} from '@packages/utilities/fp/utils';
import {
  getPageComponentFromComponentMap,
  isUserComponent,
} from '@packages/utilities/core-elements';
import {defer} from '@packages/utilities/lazy';

const ATTR_ID_PATH: ExpressionPath = [
  {in: 'Record', at: 'attr'},
  {in: 'Record', at: 'id'},
];

export type NamedElementEntry = {id: string; attrId: string};

// Returns the id attribute (data.attr) of an element
export const getElementId = (element: ExpressionElement): string => {
  return Expressions.getElementDataAt(element, ATTR_ID_PATH, 'Text', '');
};

// Returns a new id attribute to be used for an element by looking up
// existing id attributes in the component map
export const getNewElementId = (
  components: Component.ComponentMap,
  prefix: string | null = 'element'
): string => {
  const existingIds = getNamedElements(components).map((el) => el.attrId);

  const uniqueId = existingIds
    ? increment(existingIds, prefix || 'element', '-')
    : '';

  return uniqueId;
};

/**
 * Builds a fully-resolved data record for a component instance by iterating
 * over the component's type fields and filling in each value from the
 * instance override (resolving parent-prop references) or the field default.
 *
 * After this, the returned data contains concrete values for every field —
 * no unresolved Select(data, parentProp) references remain. This mirrors
 * the approach in expandComponentsUtils.applyOverridesFromInstance.
 */
const resolveInstanceData = (
  rawInstanceData: Expression,
  parentData: Expression | null,
  component: Component.ComponentType
): Expression => {
  const typeFields = Types.getRecordFields(Component.getDataType(component));
  if (!typeFields) return rawInstanceData;

  const rawRecord = Expressions.getRecord(rawInstanceData);
  const parentRecord = parentData ? Expressions.getRecord(parentData) : null;
  const defaultData = Component.getDefaultData(component);

  let changed = false;
  const resolved: Record<string, Expression> = {};

  for (const key of Object.keys(typeFields)) {
    const override = rawRecord?.[key];

    if (override) {
      const select = Expressions.getSelect(override);
      if (select && Expressions.isDataVariable(select.from)) {
        const parentValue = parentRecord?.[select.prop];
        if (parentValue) {
          resolved[key] = parentValue;
          changed = true;
          continue;
        }
      } else {
        resolved[key] = override;
        if (!rawRecord || override !== rawRecord[key]) changed = true;
        continue;
      }
    }

    const defaultExpression =
      defaultData &&
      Expressions.getIn(defaultData, [
        {in: 'Function'},
        {in: 'Record', at: key},
      ]);
    if (defaultExpression) {
      resolved[key] = defaultExpression;
      changed = true;
      continue;
    }

    const fieldType = typeFields[key];
    if (fieldType) {
      const defaultValue = Types.getDefaultValue(fieldType, undefined);
      if (defaultValue != null) {
        resolved[key] =
          typeof defaultValue === 'string'
            ? EText(defaultValue)
            : ERecord(defaultValue);
        changed = true;
        continue;
      }
    }
  }

  return changed ? ERecord(resolved) : rawInstanceData;
};

/**
 * Extracts the static text ID from an element's data.attr.id if it's a
 * plain Text expression. Returns null for Variables, Selects, or missing IDs.
 */
const getStaticAttrId = (element: ExpressionElement): string | null => {
  return (
    Expressions.getElementDataAt(element, ATTR_ID_PATH, 'Text', null) ?? null
  );
};

/**
 * Given an element from a component render whose attr.id is connected to
 * a component prop, returns the prop id. Returns `null` otherwise.
 *
 * Component props are represented as wfdl`data.propName`.
 */
const getIdAttrPropId = (element: ExpressionElement): string | null => {
  const elementData = Expressions.getElementData(element);
  const attrIdExpr = Expressions.getIn(elementData, ATTR_ID_PATH);
  if (!attrIdExpr) return null;

  const select = Expressions.getSelect(attrIdExpr);
  if (!select || !Expressions.isDataVariable(select.from)) return null;

  return select.prop ?? null;
};

/** Takes in a componentMap and returns a {elementId: idAttr} map */
export const collectIdAttributes = weakMemo(
  (components: Component.ComponentMap): {[key: string]: string} => {
    return getNamedElements(components).reduce(
      (acc, {id, attrId}) => {
        acc[id] = attrId;
        return acc;
      },
      {} as {[key: string]: string}
    );
  }
);

export const buildIdAttrMap = weakMemo((components: Component.ComponentMap) =>
  defer(() => collectIdAttributes(components))
);

type NamedElementOrInstance =
  | {kind: 'StaticNamedElement'; id: string; attrId: string}
  | {kind: 'PropNamedElement'; id: string; propId: string}
  | {kind: 'Instance'; inst: ExpressionElement};

const resumeWithNoResult = Expressions.resume(emptyArray);

const getNamedElementsAndInstances = Expressions.everything<
  NamedElementOrInstance[]
>(concat)(
  matchExpression({
    Element: (el) => {
      if (isUserComponent(el.type)) {
        return Expressions.resume([
          {
            kind: 'Instance',
            inst: el,
          },
        ]);
      }

      const staticAttrId = getStaticAttrId(el);
      if (staticAttrId) {
        return Expressions.resume([
          {
            kind: 'StaticNamedElement',
            id: el.id,
            attrId: staticAttrId,
          },
        ]);
      }

      const boundAttrPropId = getIdAttrPropId(el);
      if (boundAttrPropId) {
        return Expressions.resume([
          {
            kind: 'PropNamedElement',
            id: el.id,
            propId: boundAttrPropId,
          },
        ]);
      }

      return resumeWithNoResult;
    },
    _: constant(resumeWithNoResult),
  })
);

// Gets a list of named elements on this page (elements with an id attribute),
// including elements inside components whose ID is connected to a prop.
export const getNamedElements = weakMemo(
  (components: Component.ComponentMap): Array<NamedElementEntry> => {
    const currentPageComponent = getPageComponentFromComponentMap(components);
    if (!currentPageComponent) {
      return [];
    }

    function getComponentNamedElements(
      component: Component.ComponentType,
      context: null | {instanceData: Expression; instanceIdPath: string}
    ): NamedElementEntry[] {
      const namedElementsAndInstances = getNamedElementsAndInstances(
        Component.getRender(component)
      );

      return namedElementsAndInstances.reduce<NamedElementEntry[]>(
        (acc, namedElement) => {
          if (namedElement.kind === 'StaticNamedElement') {
            acc.push({id: namedElement.id, attrId: namedElement.attrId});
          }

          const instanceData = context ? context.instanceData : null;
          const instanceIdPath = context ? context.instanceIdPath : null;

          if (namedElement.kind === 'Instance') {
            const nestedComponent = components.get(namedElement.inst.type);
            if (nestedComponent) {
              const nestedInstanceData = resolveInstanceData(
                Expressions.getElementData(namedElement.inst),
                instanceData,
                nestedComponent
              );
              const instanceId = namedElement.inst.id;
              const nestedIdPath = instanceIdPath
                ? `${instanceIdPath}.${instanceId}`
                : instanceId;
              acc.push(
                ...getComponentNamedElements(nestedComponent, {
                  instanceData: nestedInstanceData,
                  instanceIdPath: nestedIdPath,
                })
              );
            }
          }

          if (namedElement.kind === 'PropNamedElement') {
            const instanceRecord = instanceData
              ? Expressions.getRecord(instanceData)
              : null;
            const propValue = instanceRecord?.[namedElement.propId];
            const text = propValue ? Expressions.getText(propValue) : null;
            if (text) {
              acc.push({
                id: `${instanceIdPath}.${namedElement.id}`,
                attrId: text,
              });
            }
          }

          return acc;
        },
        []
      );
    }

    return getComponentNamedElements(currentPageComponent, null);
  }
);
